import { useCallback, useEffect, useState } from "react";
import { ImageOff, Loader2, PlayCircle, Star, Wand2 } from "lucide-react";
import type { GrowthIntelligenceAd, SpyAd } from "../types/adsSpy.types";
import { growthIntelligenceService, mapGrowthAdToSpyAd } from "../services/growthIntelligence.service";

interface GrowthFavoritesViewProps {
  onCreateSimilar: (ad: SpyAd) => void;
}

export default function GrowthFavoritesView({ onCreateSimilar }: GrowthFavoritesViewProps) {
  const [ads, setAds] = useState<GrowthIntelligenceAd[]>([]);
  const [loading, setLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());

  const loadFavorites = useCallback(async () => {
    setLoading(true);
    try {
      const data = await growthIntelligenceService.getFavorites({ country: "CO", limit: 80 });
      setAds(data);
    } catch (err) {
      console.error("Error loading growth favorites:", err);
      setAds([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  const handleRemove = async (adId: number) => {
    const previous = ads;
    setAds((current) => current.filter((ad) => ad.id !== adId));
    try {
      await growthIntelligenceService.removeFavorite(adId);
    } catch (err) {
      console.error("Error removing growth favorite:", err);
      setAds(previous);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16" data-testid="growth-favorites-loading">
        <Loader2 className="h-5 w-5 animate-spin text-fluxi" />
      </div>
    );
  }

  if (ads.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="growth-favorites-empty">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50">
          <Star className="h-7 w-7 text-amber-400" />
        </div>
        <p className="mt-4 text-sm font-semibold text-gray-800">No hay favoritos del radar todavía</p>
        <p className="mt-1 max-w-sm text-xs text-gray-400">
          Guarda creativos desde el radar para volver a ellos cuando quieras crear anuncios similares.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="growth-favorites-view">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Favoritos del radar</h3>
        <p className="mt-1 text-xs text-gray-400">Creativos guardados desde las marcas recomendadas.</p>
      </div>

      <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {ads.map((ad) => {
          const mediaUrl = ad.thumbnail_url || ad.image_url || "";
          const isVideo = ad.media_type === "Video" && Boolean(ad.video_url);
          const hasImage = mediaUrl && !imageErrors.has(ad.id);
          return (
            <article key={ad.id} className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm" data-testid={`growth-favorite-${ad.id}`}>
              <div className="relative aspect-square bg-gray-100">
                {isVideo && !imageErrors.has(ad.id) ? (
                  <video
                    src={ad.video_url || undefined}
                    poster={mediaUrl || undefined}
                    className="h-full w-full object-cover"
                    controls
                    muted
                    playsInline
                    preload="metadata"
                    onError={() => setImageErrors((current) => new Set(current).add(ad.id))}
                  />
                ) : hasImage ? (
                  <img
                    src={mediaUrl}
                    alt={ad.headline || "Ad"}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    onError={() => setImageErrors((current) => new Set(current).add(ad.id))}
                  />
                ) : (
                  <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-400">
                    <ImageOff className="h-6 w-6" />
                    <span className="text-xs">Sin imagen</span>
                  </div>
                )}

                {isVideo && !imageErrors.has(ad.id) && (
                  <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-black/55 px-2 py-1 text-[11px] font-semibold text-white">
                    <PlayCircle className="h-3 w-3" />
                    Video
                  </span>
                )}

                <button
                  type="button"
                  onClick={() => handleRemove(ad.id)}
                  className="absolute right-2.5 top-2.5 flex h-8 w-8 items-center justify-center rounded-full bg-white/85 shadow-sm backdrop-blur transition hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-fluxi/40"
                  data-testid={`growth-favorite-remove-${ad.id}`}
                >
                  <Star className="h-4 w-4" color="#E08700" fill="#E08700" />
                </button>
              </div>

              <div className="space-y-3 p-3">
                <div>
                  <p className="text-xs font-medium text-gray-400">{ad.brand_name}</p>
                  <p className="mt-1 line-clamp-2 text-sm font-semibold leading-snug text-gray-950">
                    {ad.headline || ad.ad_copy || "Creativo sin texto"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onCreateSimilar(mapGrowthAdToSpyAd(ad))}
                  className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-xl bg-fluxi px-3 text-xs font-semibold text-white transition hover:bg-[#4545D8] focus:outline-none focus-visible:ring-2 focus-visible:ring-fluxi/40"
                  data-testid={`growth-favorite-create-${ad.id}`}
                >
                  <Wand2 className="h-3.5 w-3.5" />
                  Crear similar
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
